import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import DashboardHeader from "@/components/DashboardHeader";
import DashboardFooter from "@/components/DashboardFooter";
import { useOptionalMerchant } from "@/lib/useMerchant";
import BackToDashboard from "@/components/BackToDashboard";

/**
 * Plain-English reference for every status badge merchants see across the
 * dashboard (Chats, Orders, Returns, Customer history).
 * Pulled directly from Dashboard.jsx's STATE_MEANINGS / StateBadge styles
 * and CustomerHistorySheet's Badge/STATE_COLORS, split into the three
 * separate domains that actually exist under the hood — chat state, order
 * status, and return status — because a couple of keys (like "rejected")
 * are reused across domains with different meanings.
 */

const pill = "inline-flex rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.12em]";

const CHAT_STATES = [
  { state: "new", color: "bg-ink/10 text-ink", meaning: "The customer just started chatting. They haven't picked a product yet." },
  { state: "awaiting_product_selection", color: "bg-lilac text-ink", meaning: "The bot showed the customer its product menu or search results and is waiting for them to pick one." },
  { state: "awaiting_size", color: "bg-lilac text-ink", meaning: "The bot asked the customer to confirm which size they want and is waiting on a reply." },
  { state: "awaiting_quantity", color: "bg-lilac text-ink", meaning: "The bot asked how many of an item they want and is waiting on a reply." },
  { state: "awaiting_add_more", color: "bg-lilac text-ink", meaning: "The bot asked if the customer wants to add anything else to their cart and is waiting on a reply." },
  { state: "confirming_address", color: "bg-lilac text-ink", meaning: "The bot is collecting or confirming the customer's delivery address." },
  { state: "awaiting_payment", color: "bg-peach text-ink", meaning: "A payment link has been sent. The bot is waiting for the customer to pay." },
  { state: "awaiting_return_reason", color: "bg-peach text-ink", meaning: "The bot has asked the customer why they want to return an item and is waiting on their answer." },
  { state: "awaiting_support_ig_handle", color: "bg-peach text-ink", meaning: "The customer asked to speak to a human and the bot doesn't have their Instagram handle saved. It's asking for it before handing the chat over to you — once they reply (or don't), it lands in Stuck either way." },
  { state: "manual_review", color: "bg-hotpink text-white", meaning: "The bot got stuck (couldn't understand a reply, or hit an edge case) and handed the conversation over to you. Needs a manual reply." },
  { state: "blocked_bot_loop", color: "bg-hotpink text-white", meaning: "The bot has permanently stopped replies to this customer because it detected an unusually high volume of messages in a short time — usually spam or an accidental loop, not a real order." },
  { state: "order_placed / completed", color: "bg-mint text-ink", meaning: "The order has been placed and confirmed. The conversation is done — nothing further needed from you here." },
  { state: "awaiting_ig_handle_optional", color: "bg-lilac text-ink", meaning: "The order is already placed and paid — this is a courtesy follow-up. The bot asked for the customer's Instagram handle (just so you have it on file to reach them) and is waiting on a reply. Doesn't hold up or affect the order." },
  { state: "awaiting_ig_handle_optional_retry", color: "bg-lilac text-ink", meaning: "The customer's first reply to the handle request didn't look like a valid Instagram username, so the bot asked once more." },
  { state: "awaiting_ig_handle_optional_final", color: "bg-lilac text-ink", meaning: "The bot's last attempt at getting a valid handle — if this reply also doesn't look right, it gives up and flags it for your team instead of asking again." },
  { state: "awaiting_name", color: "bg-lilac text-ink", meaning: "The bot asked for the customer's name and is waiting on a reply." },
  { state: "awaiting_phone", color: "bg-lilac text-ink", meaning: "The bot asked for the customer's phone number and is waiting on a reply." },
  { state: "awaiting_email", color: "bg-lilac text-ink", meaning: "The bot asked for the customer's email (digital orders only) and is waiting on a reply." },
  { state: "awaiting_country", color: "bg-lilac text-ink", meaning: "The bot asked for the customer's country and is waiting on a reply." },
  { state: "awaiting_state", color: "bg-lilac text-ink", meaning: "The bot asked for the customer's state and is waiting on a reply." },
  { state: "awaiting_city", color: "bg-lilac text-ink", meaning: "The bot asked for the customer's city and is waiting on a reply." },
  { state: "awaiting_pincode", color: "bg-lilac text-ink", meaning: "The bot asked for the customer's PIN/postal code and is waiting on a reply." },
  { state: "awaiting_address_line", color: "bg-lilac text-ink", meaning: "The bot asked for the customer's street address and is waiting on a reply." },
  { state: "choosing_edit_fields", color: "bg-lilac text-ink", meaning: "The customer asked to edit their saved details and is picking which field to change." },
  { state: "awaiting_reuse_confirmation", color: "bg-lilac text-ink", meaning: "The customer has saved details on file — the bot asked if they want to reuse them for this order." },
  { state: "awaiting_coupon_code", color: "bg-lilac text-ink", meaning: "The bot asked if the customer has a coupon code and is waiting on a reply." },
  { state: "awaiting_category_selection", color: "bg-lilac text-ink", meaning: "The bot sent the category list and is waiting for the customer to pick one." },
  { state: "awaiting_return_order_selection", color: "bg-peach text-ink", meaning: "The customer has more than one order eligible for return — the bot asked which one." },
  { state: "awaiting_return_item_selection", color: "bg-peach text-ink", meaning: "The bot asked which item(s) on the order the customer wants to return." },
  { state: "awaiting_return_quantity", color: "bg-peach text-ink", meaning: "The bot asked how many units of the item the customer wants to return." },
  { state: "awaiting_exchange_order_selection", color: "bg-lilac text-ink", meaning: "The customer has more than one order eligible for exchange — the bot asked which one." },
  { state: "awaiting_exchange_item_selection", color: "bg-lilac text-ink", meaning: "The bot asked which item on the order the customer wants to exchange." },
  { state: "awaiting_exchange_size_selection", color: "bg-lilac text-ink", meaning: "The bot showed available sizes for the exchange and is waiting for the customer to pick one." },
  { state: "awaiting_support_confirmation", color: "bg-peach text-ink", meaning: "The customer asked for a human — the bot is confirming they want to be handed over before proceeding." },
  { state: "awaiting_support_reason", color: "bg-peach text-ink", meaning: "The bot asked why the customer wants a human and is waiting on a reply." },
];

const ORDER_STATES = [
  { state: "pending_payment", color: "bg-peach text-ink", meaning: "The order exists but the customer hasn't paid yet." },
  { state: "awaiting_manual_payment", color: "bg-peach text-ink", meaning: "The customer chose to pay manually (UPI ID or bank transfer) and hasn't confirmed payment yet." },
  { state: "pending_manual_verification", color: "bg-lilac text-ink", meaning: "The customer says they've paid manually and uploaded a screenshot. It's sitting in your queue for you to Confirm or Reject." },
  { state: "link_failed", color: "bg-hotpink text-white", meaning: "Checkout DM tried to generate a Razorpay payment link for this order and it failed. Needs your attention — the customer can't pay until this is fixed." },
  { state: "paid", color: "bg-mint text-ink", meaning: "Payment has been received and confirmed. This is a completed sale." },
  { state: "paid_manual_review", color: "bg-mint text-ink", meaning: "The customer reported paying manually (UPI ID or bank transfer) and you confirmed it. Functionally the same as Paid." },
  { state: "rejected", color: "bg-hotpink text-white", meaning: "This order was rejected — usually because a manually-reported payment couldn't be verified. This is different from a rejected return (see below)." },
  { state: "returned", color: "bg-mint text-ink", meaning: "This order was fully returned and refunded — every item on it was covered." },
  { state: "partially_returned", color: "bg-peach text-ink", meaning: "Some items from this order were returned and refunded, but not all of them — the rest of the order still stands." },
];

const RETURN_STATES = [
  { state: "requested", color: "bg-peach text-ink", meaning: "The customer has asked to return an item. It's waiting on your Approve/Reject decision." },
  { state: "approved", color: "bg-mint text-ink", meaning: "You approved the return. Stock is restocked automatically and the customer is notified." },
  { state: "return_rejected", color: "bg-hotpink text-white", meaning: "You declined the return request. The customer is notified, along with your return policy." },
];

const EXCHANGE_STATES = [
  { state: "exchange_requested", color: "bg-[#4C1D95] text-white", meaning: "The customer has asked to swap an item for a different size. It's waiting on your Approve/Reject decision — this can only be approved once the order is delivered." },
  { state: "exchanged", color: "bg-mint text-ink", meaning: "You approved the exchange. Stock for the new size is swapped automatically — you'll still need to ship it to the customer yourself." },
  { state: "exchange_rejected", color: "bg-hotpink text-white", meaning: "You declined the exchange request. The customer is notified." },
];

function StatusRow({ state, color, meaning }) {
  return (
    <div className="flex flex-col gap-2 border-b border-ink/5 py-4 last:border-b-0 sm:flex-row sm:items-start sm:gap-6">
      <div className="sm:w-56 sm:flex-none">
        <span className={`${pill} ${color}`}>{state.replace(/_/g, " ")}</span>
      </div>
      <p className="text-sm text-ink/70">{meaning}</p>
    </div>
  );
}

function Section({ title, subtitle, rows, extraRow }) {
  return (
    <div className="rounded-[2rem] border border-ink/10 bg-white p-6 md:p-8">
      <p className="text-xs font-bold uppercase tracking-[0.2em] text-hotpink">{title}</p>
      <p className="mt-1 text-sm text-ink/60">{subtitle}</p>
      <div className="mt-5">
        {rows.map((r) => (
          <StatusRow key={r.state} {...r} />
        ))}
        {extraRow}
      </div>
    </div>
  );
}

export default function StatusGuide() {
  const { me } = useOptionalMerchant();

  const statusSchema = {
    "@context": "https://schema.org",
    "@type": "DefinedTermSet",
    name: "Checkout DM Status Badges",
    hasDefinedTerm: [...CHAT_STATES, ...ORDER_STATES, ...RETURN_STATES].map((s) => ({
      "@type": "DefinedTerm",
      name: s.state,
      description: s.meaning,
    })),
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://checkoutdm.com/" },
      { "@type": "ListItem", position: 2, name: "Status Badges Guide", item: "https://checkoutdm.com/status-guide" },
    ],
  };

  return (
    <div className="min-h-screen bg-cream">
      <SEO
        title="Status Badges Guide | Checkout DM"
        description="What every status badge means in your Chats, Orders, Returns, and Customer History tabs."
        path="/status-guide"
        structuredData={[statusSchema, breadcrumbSchema]}
      />
      <DashboardHeader me={me} />

      <main className="mx-auto max-w-4xl px-6 pt-10 md:px-10">
        {me && <BackToDashboard />}
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-hotpink">Help</p>
        <h1 className="mt-2 font-display text-4xl font-black tracking-tighter">
          What every status badge means
        </h1>
        <p className="mt-3 max-w-2xl text-ink/70">
          These are the same colored badges you see in your Chats, Orders,
          Returns, and Customer History tabs — grouped here with a plain
          explanation of each one, so you can act on them at a glance.{" "}
          <Link to="/analytics-guide" className="font-semibold text-hotpink hover:underline">
            Looking for what an Analytics number means instead? →
          </Link>
        </p>

        <div className="mt-8 space-y-6">
          <Section
            title="Chat status"
            subtitle="Shown in the Chats tab — tracks where the bot is in a conversation with a customer."
            rows={CHAT_STATES}
          />

          <Section
            title="Order status"
            subtitle="Shown in the Orders tab and Customer History — tracks payment for a placed order."
            rows={ORDER_STATES}
          />

          <div className="rounded-[2rem] border border-ink/10 bg-white p-6 md:p-8">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-hotpink">How the return window works</p>
            <p className="mt-1 text-sm text-ink/60">
              Why a return request might get accepted (or rejected) on a date that doesn't line up with the order date alone.
            </p>
            <div className="mt-5 space-y-4 text-sm text-ink/70">
              <p>
                Your <span className="font-semibold text-ink">Return window</span> (set in Profile) is the number of
                days a customer has to request a return, but what it counts <em>from</em> depends on the order.
              </p>
              <p>
                For a physical order, once you mark it Shipped, the window counts from your ship date plus a 2-day
                cushion for transit — so customers get your full Return window from roughly when the order likely
                arrived, not from when they paid. Until you mark an order Shipped, the customer can ask to cancel it
                at any time — there's no countdown running yet.
              </p>
              <p>
                For a digital order, the window always counts from the order date, since there's no shipping involved.
              </p>
              <p>
                This is why two orders placed on the same day can show different return eligibility: the one that shipped
                faster started its countdown sooner.
              </p>
            </div>
          </div>

          <Section
            title="Return status"
            subtitle="Shown in the Returns tab — tracks a customer's return request after an order was paid."
            rows={RETURN_STATES}
            extraRow={
              <StatusRow
                state="Partial return"
                color="bg-lilac text-ink"
                meaning="Not a status on its own — this tag appears next to a return's status badge when only some items from the order are being returned, not the whole order."
              />
            }
          />
          <Section
            title="Exchange status"
            subtitle="Shown in the Returns tab and Customer History — tracks a customer's size-exchange request. Only available once the order is delivered."
            rows={EXCHANGE_STATES}
          />
        </div>
      </main>

      <div className="mt-10">
        <DashboardFooter />
      </div>
    </div>
  );
}
